import React, { useState } from 'react';
import { View, TouchableOpacity, Image as RNImage, ActivityIndicator, Platform, ScrollView } from 'react-native';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import BottomSheet from '../../../../components/BottomSheet';
import Input from '../../../../components/Forms/Input';
import IconButton from '../../../../components/IconButton';
import useFormSubmit from '../../../../hooks/useFormSubmit';
import api from '../../../../utils/api';


export default function NewTicketModal({ visible, communityId, onRequestClose, onCreated }: any) {
  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: { location: '', description: '', category_uuid: '' },
  });
  const categoryUuid = watch('category_uuid');

  // Vigilancia siempre puede elegir categoría; si la comunidad no tiene, no se muestra.
  const categoriesQuery = useQuery<any>({
    queryKey: ['guard.ticket-categories', communityId],
    queryFn: () => api.get(`/communities/${communityId}/ticket-categories`).then(r => r.data),
    enabled: !!communityId && visible,
  });
  const categories: any[] = categoriesQuery.data ?? [];

  const [files, setFiles] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);

  const { submitForm, isSubmitting } = useFormSubmit({
    onSuccess: (data: any) => {
      reset();
      setFiles([]);
      onCreated?.(data);
    },
    onError: () => {},
    successToastMessage: 'Incidencia creada',
    endpoint: `/communities/${communityId}/tickets`,
  });

  const takePhoto = async () => {
    const perm = await ImagePicker.requestCameraPermissionsAsync();
    if (!perm.granted) {
      alert('Necesitamos permiso de cámara');
      return;
    }

    const result = await ImagePicker.launchCameraAsync({ quality: 0.7 });

    if (result.canceled || !result.assets?.[0]) return;
    await uploadAsset(result.assets[0]);
  };

  const uploadAsset = async (asset: ImagePicker.ImagePickerAsset) => {
    setUploading(true);
    try {
      const fd = new FormData();
      const uri = asset.uri;
      const name = uri.split('/').pop() ?? 'image.jpg';
      const ext = /\.(\w+)$/.exec(name)?.[1] ?? 'jpg';
      const type = `image/${ext}`;

      if (Platform.OS === 'web') {
        const blob = await (await fetch(uri)).blob();
        fd.append('image', blob, name);
      } else {
        // @ts-ignore RN file shape
        fd.append('image', { uri, name, type });
      }

      const res = await api.post(`/communities/${communityId}/images`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFiles(prev => [...prev, res.data]);
    } catch (err: any) {
      alert(err?.response?.data?.message ?? 'Error subiendo imagen');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (uuid: string) => {
    setFiles(prev => prev.filter(f => f.uuid !== uuid));
  };

  const onSave = (data: any) => {
    // El backend no tiene un campo `location` separado, así que lo prefijamos a la descripción.
    const description = data.location
      ? `📍 ${data.location}\n\n${data.description ?? ''}`.trim()
      : data.description;

    submitForm({
      description,
      category_uuid: data.category_uuid || null,
      file_uids: files.map(f => f.uuid),
    });
  };

  return (
    <BottomSheet visible={visible} onRequestClose={onRequestClose} height={640}>
      <ScrollView contentContainerStyle={tw`pt-5 px-5 pb-8`}>
        <Text style={tw`mb-5 text-lg font-bold text-primary text-center`}>Nuevo Reporte de Incidencia</Text>

        {categories.length > 0 && (
          <>
            <Text style={tw`text-gray-500 mb-2`}>Categoría</Text>
            <View style={tw`flex-row flex-wrap gap-2 mb-4`}>
              {categories.map((c: any) => {
                const active = categoryUuid === c.uuid;
                return (
                  <TouchableOpacity
                    key={c.uuid}
                    onPress={() => setValue('category_uuid', active ? '' : c.uuid)}
                    style={tw.style(`px-3 py-1.5 rounded-full border`, active ? 'border-transparent bg-primary' : 'border-gray-200 bg-white')}
                  >
                    <Text style={tw.style(`text-xs font-500`, active ? 'text-white' : 'text-gray-700')}>{c.name}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </>
        )}

        <Text style={tw`text-gray-500 mb-1`}>¿Dónde sucede?<Text style={tw`text-red-500`}>*</Text></Text>
        <Input
          control={control}
          name="location"
          placeholder="Ej. Frente a casa 14"
          rules={{ required: true }}
        />

        <Text style={tw`text-gray-500 mb-1`}>Descripción:<Text style={tw`text-red-500`}>*</Text></Text>
        <Input
          control={control}
          name="description"
          placeholder="Describe la incidencia con detalles"
          multiline
          numberOfLines={4}
          rules={{ required: true }}
        />

        <Text style={tw`text-gray-500 mb-2`}>Evidencia fotográfica:</Text>
        <View style={tw`items-center mb-5`}>
          {files.length > 0 && (
            <View style={tw`flex-row flex-wrap gap-2 mb-3 self-stretch`}>
              {files.map(f => (
                <View key={f.uuid} style={tw`relative`}>
                  <RNImage source={{ uri: f.url }} style={tw`w-16 h-16 rounded-lg`} />
                  <TouchableOpacity
                    onPress={() => removeFile(f.uuid)}
                    style={tw`absolute -top-1 -right-1 bg-white rounded-full border border-gray-200 w-5 h-5 items-center justify-center`}
                  >
                    <Ionicons name="close" size={12} color="#374151" />
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          )}

          {uploading ? (
            <View style={tw`w-20 h-20 rounded-lg items-center justify-center`}>
              <ActivityIndicator color={tw.color('primary')} />
            </View>
          ) : (
            <TouchableOpacity
              onPress={takePhoto}
              style={tw`w-20 h-20 rounded-lg border-2 border-primary items-center justify-center`}
            >
              <Ionicons name="camera-outline" size={28} color={tw.color('primary')} />
              <Text style={tw`text-xs text-primary mt-1`}>Tomar foto</Text>
            </TouchableOpacity>
          )}
        </View>

        <IconButton
          disabled={isSubmitting || uploading}
          title="Crear incidencia"
          onPress={handleSubmit(onSave)}
        />
      </ScrollView>
    </BottomSheet>
  );
}
